import { Injectable, Injector, computed, inject, ApplicationRef } from '@angular/core';
import { clearGlobalStore, GlobalStore } from '@store/global.state';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { UsersState } from './users.state';
import { ApiService } from '@services/api.service';
import { EndPoints } from '@services/EndPoints';
import { UserRole } from '@enums/user.enum';
import { AdminProvider, ClientsProvider } from '@providers';
import { TagDto } from '@dtos/tag.dto';
import { ImportFileProvider } from '@providers2/importFile.provider';
import { ImportDataUrlsDto } from '@dtos/import-data.dto';

@Injectable({
  providedIn: 'root'
})
export class StoreService {

  // Singleton estático
  public static instance: StoreService | null = null;

  // Propiedad estática global para acceso desde cualquier lugar


  // Store global de la aplicación
  readonly global = inject(GlobalStore);
  readonly router = inject(Router)
  readonly users = inject(UsersState);
  readonly api = inject(ApiService);
  readonly adminProvider = inject(AdminProvider);
  readonly clientProvider = inject(ClientsProvider);
  readonly importFileProvider = inject(ImportFileProvider);
  readonly appRef = inject(ApplicationRef);
  private injector = inject(Injector);
  get isDebug() {
    return !environment.production;
  }

  constructor() {
    // Establecer la instancia singleton
    if (StoreService.instance) {
      return StoreService.instance;
    }
    StoreService.instance = this;

    // Sincronizar sesión en tiempo real entre pestañas de forma puramente pasiva
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (event) => {
        if (event.key === 'roadit_global_session' || event.key === null) {
          this.global.syncFromCrossTab();
          // Forzar tick de detección de cambios en modo Zoneless tras sincronizar
          setTimeout(() => this.appRef.tick(), 0);
        }
      });
    }

    // Inicializar desde almacenamiento al arrancar la aplicación
    this.global.initializeFromStorage();
    this.global.tryAutoLogin().then(() => {});
    this.getImportDataUrls().then(() => {});
    this.global.getUserLoggedOutObservable().subscribe(async (loggedIn) => {
      const publicRoutes = ['/login', '/resetpassword', "/duplicate-tab"];
      const currentPath = window.location.pathname;

      if (!loggedIn) {
        if (!publicRoutes.some(r => currentPath.startsWith(r))) {
          this.router.navigate(['/login']);
          // Forzar tick de detección de cambios en Zoneless tras redirigir a login
          setTimeout(() => this.appRef.tick(), 0);
        }
      } else {
        // Si el usuario está autenticado y se encuentra en una ruta pública (ej. login), redirigir a la aplicación
        if (publicRoutes.some(r => currentPath.startsWith(r)) || currentPath === '/') {
          this.router.navigate(['/transports']);
          // Forzar tick de detección de cambios en Zoneless tras redirigir a transports
          setTimeout(() => this.appRef.tick(), 0);
        }

        if (this.global.user().user.role === UserRole.ADMIN) {
          this.loadUsersAdmin().then(() => {});
          await this.getGroups();
        }

        if (this.global.tags().length === 0) {
          this.loadTags();
        }

        // Forzar tick para pintar los menús/cabeceras que dependen del rol del usuario
        setTimeout(() => this.appRef.tick(), 0);
      }
    });
  }
  async loadUsersAdmin() {
    const adminsList = await this.adminProvider.getAdmins({});
    if (adminsList && adminsList.results && adminsList.results.length > 0) {
      this.global.updateState({
        usersAdmin: adminsList.results.map(admin => {
          return { id: admin.id!, name: admin.name + ' ' + admin.last_name, otherData: admin };
        })
      });
    }
  }
  async getImportDataUrls() {
    const importDataUrls = await this.importFileProvider.GetImportDataUrls();
    if (importDataUrls) {
      this.global.updateState({ importDataUrls: ImportDataUrlsDto.fromJson(importDataUrls) });
    }
  }
  async getGroups() {
    const groups = await this.clientProvider.getGroups({ page_size: 999 });
    if (groups && groups.length > 0) {
      this.global.updateState({
        groups: groups.map(group => {
          return { id: group.id!, name: group.name };
        })
      });
    }
  }

  // Método para obtener el estado completo de todos los stores (útil para debugging)
  getFullState() {
    return {
      global: {
        user: this.global.user(),
        language: this.global.language(),
        userFull: this.global.userFull(),
        menuCollapsed: this.global.menuCollapsed(),
        isAuthenticated: this.global.isAuthenticated()
      }
    };
  }

  // Método para resetear todos los stores
  resetAllStores() {
    this.global.logout();
    clearGlobalStore(this.injector);
  }
  async loadTags() {
    try {
      const response = await this.api.get<any[]>({ url: EndPoints.getTags });
      if (response && response.length > 0) {
        //CONVERTIR CADA TAG A INSTANCIA DE TagDto
        const tagsDto = response.map(tagData => TagDto.fromJson(tagData));
        this.global.updateState({ tags: tagsDto });
      }
    } catch (error) {
      console.error('Error loading tags:', error);
    }
  }

}
